import { ICompileService } from "angular";
import { uniq } from "lodash";
import { RecordDataSource } from "../../../../../../records/grids";
import { FilterProvider } from "../../../../../../records/grids/columns/filters/FilterProvider";
import { LookupFilter } from "../../../../../../records/grids/columns/filters/subFilters/LookupFilter";
import { OptionsFilter } from "../../../../../../records/grids/columns/filters/subFilters/OptionsFilter";
import {
  GridSettingsService,
  vsName as GridSettingsServiceName,
  IGridSettings,
} from "../../../../../../records/grids/settings/gridSettingsService";
import {
  booleanValueGetterFactory,
  lookupValueGetterFactory,
  moneyValueGetterFactory,
  percentageValueGetterFactory,
} from "../../../../../../records/grids/cells/valueGetters";
import { Record } from "../../../../../../records/server/crud";
import { IQuery } from "../../../../../../records/server/query";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../../../../../records/server/serverUtility";
import { ColDef } from "../../../../../../records/grids/columns/colDef";
import { ProgressivePermissions } from "../../../../../../machines.progressives/permissions";

const gridId = Symbol("machineDetailProgressiveConfigurationAdd");
const apiUrl = "/api/machines/progressives/ProgressiveDefinitionMachine";
const progressiveDefinitionUrl =
  "/api/machines/progressives/ProgressiveDefinition";
const progressiveMeterReadingSettingUrl =
  "/api/machines/progressives/ProgressiveMeterReadingSetting";
const progressiveControllerTypeUrl =
  "/api/machines/progressives/ProgressiveControllerType";

export class MachinesDetailProgressiveConfigurationEditViewController {
  static $inject = [
    "machineInfo",
    GridSettingsServiceName,
    "$compile",
    ServerUtilityName,
    "currentProgressiveDefinitions",
    "date",
  ];

  readonly gridDataSource: RecordDataSource;
  readonly columns: ColDef[];
  readonly gridSettings: IGridSettings;
  readonly defaultQuery: IQuery;
  readonly initialProgressiveDefinitionIds: number[];
  currentProgressivesOnly: boolean = false;

  private readonly machineChange: any;
  readonly machineInfoCssClass: string;

  constructor(
    readonly machineInfo: any,
    gridSettingsService: GridSettingsService,
    $compile: ICompileService,
    private readonly serverUtility: ServerUtility,
    readonly currentProgressiveDefinitionMachines: Record[],
    private readonly date: Date
  ) {
    this.machineChange = machineInfo.machineChange;
    this.initialProgressiveDefinitionIds = uniq(
      currentProgressiveDefinitionMachines.map(
        (pdm) => <number>pdm.ProgressiveDefinitionId
      )
    );
    this.gridDataSource = new RecordDataSource(
      serverUtility,
      progressiveDefinitionUrl,
      true
    );
    this.gridSettings = gridSettingsService.getGridSettings(gridId);
    this.defaultQuery = {};
    this.columns = [
      {
        colId: "select",
        headerName: "Select",
        template: `
          <input
            type="checkbox"
            ng-click="recordGridController.toggleRowSelection(data)"
            ng-checked="recordGridController.rowSelected(data)"
          />`,
        suppressSorting: true,
      },
      {
        field: "Id",
        headerName: "Id",
        filter: new FilterProvider("Id", $compile)
          .defaultNumberFilters()
          .getGridFilterFactory(),
        hide: true,
      },
      {
        field: "Name",
        headerName: "Name",
        filter: new FilterProvider("Name", $compile)
          .defaultStringFilters()
          .addFilter(
            "=",
            new LookupFilter({
              displayName: "=",
              queryFieldNames: ["Id"],
              serverQuery: (search: string) =>
                serverUtility.lookupQueryWrapped({
                  url: progressiveDefinitionUrl,
                  search,
                }),
            })
          )
          .getGridFilterFactory(),
      },
      {
        field: "ManufacturerName",
        headerName: "Manufacturer",
        filter: new FilterProvider("ManufacturerName", $compile)
          .defaultStringFilters()
          .addFilter(
            "=",
            new LookupFilter({
              displayName: "=",
              queryFieldNames: ["ManufacturerId"],
              serverQuery: (search: string) => {
                return serverUtility.lookupQuery({
                  url: "/api/machines/manufacturers",
                  search,
                });
              },
            })
          )
          .getGridFilterFactory(),
      },
      {
        field: "ProgressiveMeterReadingSettingName",
        headerName: "Meter Reading Setting",
        filter: new FilterProvider(
          "ProgressiveMeterReadingSettingName",
          $compile
        )
          .defaultStringFilters()
          .addFilter(
            "=",
            new LookupFilter({
              displayName: "=",
              queryFieldNames: ["ProgressiveMeterReadingSettingId"],
              serverQuery: (search: string) =>
                serverUtility.lookupQueryWrapped({
                  url: progressiveMeterReadingSettingUrl,
                  search,
                }),
            })
          )
          .getGridFilterFactory(),
      },
      {
        field: "ProgressiveMeterReadingSettingMeterReadingRequired",
        headerName: "Meter Reading Required",
        valueGetter: booleanValueGetterFactory(),
        filter: new FilterProvider(
          "ProgressiveMeterReadingSettingMeterReadingRequired",
          $compile
        )
          .defaultBooleanFilters()
          .getGridFilterFactory(),
      },
      {
        field: "ProgressiveMeterReadingSettingComparisonField",
        headerName: "Comparison Field",
        valueGetter: lookupValueGetterFactory({
          lookup: { ResetBase: "Reset Base", LevelReading: "Level Reading" },
        }),
        filter: new FilterProvider(
          "ProgressiveMeterReadingSettingComparisonField",
          $compile
        )
          .addFilter(
            "=",
            new OptionsFilter({
              displayName: "=",
              options: [
                {
                  display: "Reset Base",
                  value: "ResetBase",
                },
                {
                  display: "Level Reading",
                  value: "LevelReading",
                },
              ],
              queryFieldNames: [
                "ProgressiveMeterReadingSettingComparisonField",
              ],
              includeNullValue: true,
            })
          )
          .getGridFilterFactory(),
      },
      {
        field: "ProgressiveMeterReadingSettingComparisonValue",
        headerName: "Comparison Value",
        filter: new FilterProvider(
          "ProgressiveMeterReadingSettingComparisonValue",
          $compile
        )
          .defaultNumberFilters()
          .getGridFilterFactory(),
        valueGetter: moneyValueGetterFactory(),
      },
      {
        field: "ProgressiveMeterReadingSettingEveryXDays",
        headerName: "Read every X days",
        filter: new FilterProvider(
          "ProgressiveMeterReadingSettingEveryXDays",
          $compile
        )
          .defaultNumberFilters()
          .getGridFilterFactory(),
      },
      {
        field: "ProgressiveMeterReadingSettingAllReadingsRequiredDayOfMonth",
        headerName: "All Readings Required on Day",
        filter: new FilterProvider(
          "ProgressiveMeterReadingSettingAllReadingsRequiredDayOfMonth",
          $compile
        )
          .defaultNumberFilters()
          .getGridFilterFactory(),
      },
      {
        field: "Status",
        headerName: "Status",
        filter: new FilterProvider("Status", $compile)
          .addFilter(
            "=",
            new OptionsFilter({
              displayName: "=",
              options: [
                { display: "Active", value: "Active" },
                { display: "Inactive", value: "Inactive" },
              ],
              queryFieldNames: ["Status"],
              includeNullValue: true,
            })
          )
          .getGridFilterFactory(),
      },
      {
        field: "ProgressiveControllerTypeName",
        headerName: "Progressive Controller",
        filter: new FilterProvider("ProgressiveControllerTypeName", $compile)
          .defaultStringFilters()
          .addFilter(
            "=",
            new LookupFilter({
              displayName: "=",
              queryFieldNames: ["ProgressiveControllerTypeId"],
              serverQuery: (search: string) =>
                serverUtility.lookupQueryWrapped({
                  url: progressiveControllerTypeUrl,
                  search,
                }),
            })
          )
          .getGridFilterFactory(),
      },
      {
        field: "Version",
        headerName: "Version",
        filter: new FilterProvider("Version", $compile)
          .defaultStringFilters()
          .getGridFilterFactory(),
      },
      {
        field: "TotalPercentage",
        requiresPermission: ProgressivePermissions.SensitiveInfoPermission,
        headerName: "Total Percentage",
        filter: new FilterProvider("TotalPercentage", $compile)
          .defaultNumberFilters()
          .getGridFilterFactory(),
        valueGetter: percentageValueGetterFactory({ decimalPlaces: 2 }),
      },
    ];
    this.machineInfoCssClass = this.getMachineInfoCssClass();
  }

  getMachineInfoCssClass(): string {
    const currentConfiguration =
      this.machineChange.MachineConfiguration.CurrentConfiguration;
    const pendingConfiguration = this.machineChange.Date > this.date;
    return currentConfiguration
      ? "currentConfiguration"
      : pendingConfiguration
      ? "pendingConfiguration"
      : "";
  }

  gridSelectionChanged(row: Record, selected: boolean) {
    if (selected) {
      this.addProgressiveDefinitionMachine(row);
    } else {
      this.removeProgressiveDefinitionMachine(row);
    }
  }

  private addProgressiveDefinitionMachine(row: Record) {
    const progressiveDefinitionId = row.Id;
    const machineConfigurationId = this.machineChange.MachineConfiguration.Id;
    const postObject = {
      progressiveDefinitionId,
      machineConfigurationId,
      active: true,
    };
    this.serverUtility
      .postWithCrudRecordResponse({
        url: apiUrl,
        postObject,
      })
      .then((crudResponse) => {
        this.currentProgressiveDefinitionMachines.push(crudResponse.Result);
      });
  }

  private removeProgressiveDefinitionMachine(row: Record) {
    const progressiveDefinitionMachine =
      this.currentProgressiveDefinitionMachines.find(
        (pdm) => pdm.ProgressiveDefinitionId === row.Id
      );
    if (!progressiveDefinitionMachine) {
      return;
    }

    this.serverUtility
      .delete({
        url: apiUrl,
        id: progressiveDefinitionMachine.Id,
      })
      .then(() => {
        const index = this.currentProgressiveDefinitionMachines.indexOf(
          progressiveDefinitionMachine
        );
        this.currentProgressiveDefinitionMachines.splice(index, 1);
      });
  }

  currentProgressivesOnlyChanged() {
    this.defaultQuery.Ids = this.currentProgressivesOnly
      ? this.currentProgressiveDefinitionMachines.map(
          (pd) => <number>pd.ProgressiveDefinitionId
        )
      : undefined;
  }
  /*datasource="stateController.gridDataSource" 
    columns="stateController.columns" 
    grid-settings="stateController.gridSettings"
    default-query="stateController.defaultQuery" */
}
